import { format, isValid, parseISO, startOfDay } from 'date-fns';
import { User } from 'store/user/types';
import { CreateVisitDto, MultiDayCreateVisitsResponse, Visit, VisitSeriesDate, VisitStatus } from './types';
import { mapVisit, VisitWithOptionalHost } from './mappers';
import { VisitorType } from 'store/api-client/api-client.types';

export const isMultiDayCreateResponse = (response: unknown): response is MultiDayCreateVisitsResponse => {
  if (response == null || typeof response !== 'object') {
    return false;
  }
  return Array.isArray((response as MultiDayCreateVisitsResponse).visits);
};

export const getSeriesUuidFromVisit = (visit: Pick<Visit, 'series_uuid' | 'visit_series_uuid'>): string | null => {
  if (typeof visit.series_uuid === 'string' && visit.series_uuid.length > 0) {
    return visit.series_uuid;
  }
  if (typeof visit.visit_series_uuid === 'string' && visit.visit_series_uuid.length > 0) {
    return visit.visit_series_uuid;
  }
  return null;
};

export const toDateOnlyString = (value: Date | string): string => {
  const date = value instanceof Date ? value : parseISO(String(value));
  if (!isValid(date)) {
    return String(value).slice(0, 10);
  }
  return format(startOfDay(date), 'yyyy-MM-dd');
};

export const parseSeriesArrivalDates = (dates: VisitSeriesDate[]): Date[] =>
  dates
    .map((entry) => {
      const raw = entry.arrivalTime;
      const parsed = raw instanceof Date ? raw : parseISO(String(raw));
      return isValid(parsed) ? startOfDay(parsed) : null;
    })
    .filter((date): date is Date => date != null)
    .sort((left, right) => left.getTime() - right.getTime());

export const countDateDiffs = (
  originalDates: string[],
  nextDates: string[],
): { datesAddedCount: number; datesRemovedCount: number } => {
  const original = new Set(originalDates);
  const next = new Set(nextDates);
  let datesAddedCount = 0;
  let datesRemovedCount = 0;

  next.forEach((date) => {
    if (!original.has(date)) {
      datesAddedCount += 1;
    }
  });
  original.forEach((date) => {
    if (!next.has(date)) {
      datesRemovedCount += 1;
    }
  });

  return { datesAddedCount, datesRemovedCount };
};

export const mapMultiDayCreateResponseToVisit = (
  response: MultiDayCreateVisitsResponse,
  visitDto: Partial<CreateVisitDto>,
  currentUser: User | null,
): Visit => {
  const firstVisit = response.visits[0];
  const id = String(firstVisit?.uuid ?? firstVisit?.id ?? response.series_uuid ?? '');
  const arrivalDate = firstVisit?.arrival_time ?? firstVisit?.arrival_date ?? visitDto.arrival_date;
  const departureDate = firstVisit?.departure_time ?? firstVisit?.departure_date ?? visitDto.departure_date;
  const visitors =
    firstVisit?.visitors ??
    visitDto.visitors?.map((visitor) => ({
      ...visitor,
      type: VisitorType.EXTERNAL,
    }));

  const mapped: VisitWithOptionalHost = {
    id,
    email: visitDto.email ?? firstVisit?.email ?? '',
    first_name: visitDto.first_name ?? firstVisit?.first_name ?? firstVisit?.visitor_name ?? '',
    last_name: visitDto.last_name ?? firstVisit?.last_name ?? '',
    visit_status: firstVisit?.visit_status ?? firstVisit?.status ?? VisitStatus.INVITED,
    arrival_date: arrivalDate as Date,
    departure_date: departureDate as Date | undefined,
    user_id: visitDto.user_id ?? '',
    visitors,
    floor: visitDto.floor,
    suite: visitDto.suite,
    description: visitDto.description,
    email_opt_in: visitDto.email_opt_in,
    sms_opt_in: visitDto.sms_opt_in,
    series_uuid: response.series_uuid ?? firstVisit?.series_uuid ?? firstVisit?.visit_series_uuid,
    visit_series_uuid: firstVisit?.visit_series_uuid ?? response.series_uuid,
    series_type: response.series_type ?? firstVisit?.series_type,
    series_dates: visitDto.visit_dates,
  };

  return mapVisit(mapped, currentUser);
};
